import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UserService } from './user.service';
import { Router } from '@angular/router';
import { ApiService } from '../core/http/api.service';
import { LoggingService } from './logging.service';
import { UserActivity } from '../models/classes/user-activity.class';

@Injectable({
    providedIn: 'root'
})
export class ActivityTrackingService {
    private platformId = inject(PLATFORM_ID);
    private userService = inject(UserService);
    private router = inject(Router);
    private apiService = inject(ApiService);
    private loggingService = inject(LoggingService);


    //on event, send data to db to store user activity
    userActivityStorage(activity: {name: string; contextParameter?: Record<string, string>[];}) {
        if (!isPlatformBrowser(this.platformId)) return;
        const contextParameters = activity.contextParameter?.map(c => ({ "key": Object.keys(c)[0] ?? '', "value": Object.values(c)[0] ?? '' }));
        const userActivity: UserActivity = {
            "activityName": activity.name,
            "controllerName": this.router.url,
            "actionName": this.getCurrentPage(),
            "userName": this.userService.getUserEmail(),
            "isAdmin": !this.userService.isExternalUser(),
            "applicationName": "ClientScope",
            "contextParameters": contextParameters
        }
        this.apiService.updateUserActivity(userActivity).subscribe({
            error: (err) => {
                this.loggingService.log("----- User Activity Logging Error -----", err);
            }
          });
    }

    toggledAsOfDate(date: Date | string | number) {
        const activity = {
            name: 'As of date',
            contextParameter: [{'date': date.toString()}]
        }
        this.userActivityStorage(activity);
    }

    expandOrCollapseToggled(type: string, data: { name: string; masterId?: string | number; id?: string | number }, table: string, expanded = true) {
        const activity = {
          name: `${expanded ? 'Expanded' : 'Collapsed'} ${type}`,
          contextParameter: [{'name': data.name}, {'id': data?.masterId ?? data?.id}, {'table': table}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    navigateToDashboard(type: string, data: { name: string; masterId?: string | number; id?: string | number }, from: string) {
        const activity = {
            name: `${type} navigation`,
            contextParameter: [{'name': data.name}, {'id': data?.masterId ?? data?.id}, {'from': from}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    downloadSnapshot(id: string | number, name: string | number) {
        const activity = {
            name: 'Downloaded snapshot',
            contextParameter: [{'id': id}, {'name': name}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    widgetTabChange(widget: string, tabName: string) {
        const activity = {
            name: `${widget}`,
            contextParameter: [{'action': 'toggled tab'}, {'name': tabName}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    tableExpandViewToggle(table: string, name: string, expanded = true) {
        const activity = {
          name: `${expanded ? 'Expanded' : 'Collapsed'} table view`,
          contextParameter: [{'table': table}, {'name': name}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    exportToExcelClick(table: string, name: string) {
        const activity = {
            name: 'Export to excel',
            contextParameter: [{'table': table}, {'name': name}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    tourServiceStarted(clicked: boolean) {
        let context: string;
        if (clicked) {
            context = 'clicked to start'
        } else {
            context = 'started from login'
        }
        const activity = {
            name: `Tour started`,
            contextParameter: [{'context': context}]
        }
        this.userActivityStorage(activity);
    }

    useFilterTool(filterType: string, filteredInvestor: { id: string | number; name: string }) {
        const activity = {
          name: 'Selected filter',
          contextParameter: [
            {'filter': filterType},
            {'id': filteredInvestor.id},
            {'name': filteredInvestor.name}
          ] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    logout() {
        const activity = {
          name: `Log out button clicked`,
          contextParameter: [{'name': this.userService.getUserEmail()}]
        }
        this.userActivityStorage(activity);
    }

    login() {
        const activity = {
          name: `Log in`,
          contextParameter: [{'name': this.userService.getUserEmail()}]
        }
        this.userActivityStorage(activity);
    }

    submittedRequest() {
        const activity = {
          name: `Submitted request on contact page`,
        }
        this.userActivityStorage(activity);
    }

    landedOnPage(pageName: string){
        const activity = {
            name: 'Landed on a Page',
            contextParameter: [{ 'name': pageName }]
          }
          this.userActivityStorage(activity);
    }

    viewMoreSummary(id: string | number, name: string | number) {
        const activity = {
            name: 'View more summary',
            contextParameter: [{'id': id}, {'name': name}] as Record<string, string>[]
          }
          this.userActivityStorage(activity);
    }

    navigateToAssetUrl(dashboard: string, assetId: string | number, assetName: string, url: string) {
        const activity = {
            name: 'Asset URL navigation',
            contextParameter: [{'id': assetId}, {'name': assetName}, {'url': url}, {'dashboard': dashboard}] as Record<string, string>[]
        }
        this.userActivityStorage(activity);
    }

    getCurrentPage() {
        if (this.router.url?.includes('asset')) {
            return 'asset dashboard'
        } else if (this.router.url?.includes('deal')) {
            return 'deals dashboard'
        } else if (this.router.url?.includes('series')) {
            return 'series dashboard'
        } else if (this.router.url?.includes('contact')) {
            return 'contact'
        } else if (this.router.url?.includes('document-review')){
            return 'Document Review'
        } else if (this.router.url?.includes('investor')){
            return 'investor dashboard'
        } else {
            return 'login'
        }
    }
}